import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { doOpenCall } from '../../_utilities/doOpenCall'
import { formatCurrency } from '../../_utilities/format'
import {
  buildLineItemAmounts,
  computeSelectedTotals,
  getInitialSelectedLineItemIds,
  resolveIncludedLineItems,
} from '../../_utilities/lineItemSelection'
import type {
  PublicQuoteComment,
  PublicQuoteLineItem,
  PublicQuoteLoadState,
  PublicQuotePayload,
} from '../../_types/quote'
import {
  ActionsRow,
  BrandHeader,
  BrandLogo,
  BrandName,
  SentBy,
  BriefDeck,
  BriefSlide,
  BriefStage,
  BriefNav,
  BriefNavButton,
  BriefCounter,
  CommentAuthor,
  CommentBody,
  CommentBubble,
  CommentEmpty,
  CommentTextarea,
  CommentThread,
  IntroText,
  ItemsTable,
  LockedBanner,
  SelectionHint,
  NotesBlock,
  OutlineButton,
  PageBackground,
  PageContent,
  PdfLink,
  PreviewBanner,
  PreviewResetButton,
  PrimaryButton,
  QuoteCard,
  QuoteHeader,
  QuoteTitle,
  RequestLinkForm,
  RequestSentNote,
  RequestErrorNote,
  ResultBanner,
  StateCard,
  SuccessButton,
  TotalsBlock,
  TotalsRow,
  VersionTag,
} from './styled'

const DEFAULT_BRAND_COLOUR = '#6d28d9'
const DEFAULT_ACCENT_COLOUR = '#ede9fe'

const QuotePage = () => {
  const { token } = useParams<{ token: string }>()
  const [searchParameters] = useSearchParams()
  const isPreview = searchParameters.get('preview') === '1'
  const [payload, setPayload] = useState<PublicQuotePayload | null>(null)
  const [loadState, setLoadState] = useState<PublicQuoteLoadState>('loading')
  const [commentBody, setCommentBody] = useState<string>('')
  const [slideIndex, setSlideIndex] = useState<number>(0)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false)
  const [isSubmittingDecision, setIsSubmittingDecision] =
    useState<boolean>(false)
  const [previewDecision, setPreviewDecision] = useState<
    'approved' | 'declined' | null
  >(null)
  const [previewComments, setPreviewComments] = useState<PublicQuoteComment[]>(
    [],
  )
  const [requestMessage, setRequestMessage] = useState<string>('')
  const [requestState, setRequestState] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')

  const brand = payload?.brandColour || DEFAULT_BRAND_COLOUR
  const accent = payload?.accentColour || DEFAULT_ACCENT_COLOUR

  const loadQuote = useCallback(async (): Promise<void> => {
    if (!token) {
      setLoadState('notfound')
      return
    }

    const response = await doOpenCall(
      isPreview ? 'publicquote-preview' : 'publicquote-get',
      { token },
    )

    if (response.code === 200) {
      setPayload(JSON.parse(response.data) as PublicQuotePayload)
      setLoadState('ready')
    } else if (response.code === 410) {
      setLoadState('expired')
    } else if (response.code === 404) {
      setLoadState('notfound')
    } else {
      setLoadState('error')
    }
  }, [token, isPreview])

  useEffect(() => {
    loadQuote()
  }, [loadQuote])

  useEffect(() => {
    if (!payload) {
      return
    }
    const stored = payload.recipient.selectedLineItemIds
    if (Array.isArray(stored) && stored.length > 0) {
      setSelectedIds(stored)
    } else {
      setSelectedIds(getInitialSelectedLineItemIds(payload.lineItems))
    }
  }, [payload])

  useEffect(() => {
    const robotsMeta = document.createElement('meta')
    robotsMeta.name = 'robots'
    robotsMeta.content = 'noindex, nofollow'
    document.head.appendChild(robotsMeta)

    return () => {
      document.head.removeChild(robotsMeta)
    }
  }, [])

  const handleAddComment = useCallback(async (): Promise<void> => {
    if (!token || commentBody.trim().length === 0 || isSubmittingComment) {
      return
    }

    if (isPreview) {
      setPreviewComments((previous) => [
        ...previous,
        {
          authorType: 'client',
          authorName: 'You',
          body: commentBody.trim(),
          createdAt: Date.now(),
        },
      ])
      setCommentBody('')
      return
    }

    setIsSubmittingComment(true)
    const response = await doOpenCall('publicquote-comment', {
      token,
      body: commentBody.trim(),
    })
    setIsSubmittingComment(false)

    if (response.code === 200) {
      setCommentBody('')
      await loadQuote()
    }
  }, [token, commentBody, isSubmittingComment, isPreview, loadQuote])

  const handleDecision = useCallback(
    async (decision: 'approve' | 'decline'): Promise<void> => {
      if (!token || isSubmittingDecision) {
        return
      }

      if (isPreview) {
        setPreviewDecision(decision === 'approve' ? 'approved' : 'declined')
        return
      }

      setIsSubmittingDecision(true)
      const includedIds = payload
        ? resolveIncludedLineItems(payload.lineItems, selectedIds)
        : []
      const response = await doOpenCall(`publicquote-${decision}`, {
        token,
        selectedLineItemIds: includedIds,
      })
      setIsSubmittingDecision(false)

      // A 409 means the quote was locked, or this recipient already answered,
      // since the page loaded. Reloading swaps the buttons for the real state.
      if (response.code === 200 || response.code === 409) {
        await loadQuote()
      }
    },
    [token, isSubmittingDecision, isPreview, loadQuote, payload, selectedIds],
  )

  const handlePreviewReset = useCallback((): void => {
    setPreviewDecision(null)
    setPreviewComments([])
    setCommentBody('')
  }, [])

  const handleRequestLink = useCallback(async (): Promise<void> => {
    if (!token || requestState === 'sending' || requestState === 'sent') {
      return
    }

    setRequestState('sending')
    const response = await doOpenCall('publicquote-requestlink', {
      token,
      message: requestMessage.trim(),
    })

    if (response.code === 200) {
      setRequestState('sent')
    } else {
      setRequestState('error')
    }
  }, [token, requestState, requestMessage])

  const lineItems = useMemo(() => payload?.lineItems ?? [], [payload])

  const includedIdSet = useMemo(
    () => new Set(resolveIncludedLineItems(lineItems, selectedIds)),
    [lineItems, selectedIds],
  )

  const liveTotals = useMemo(
    () =>
      computeSelectedTotals(
        lineItems,
        selectedIds,
        payload?.discountAmount ?? 0,
        payload?.taxRatePercent ?? 0,
      ),
    [lineItems, selectedIds, payload],
  )

  const includedLineItems = useMemo(
    () => lineItems.filter((lineItem) => includedIdSet.has(lineItem.id)),
    [lineItems, includedIdSet],
  )

  const lineItemAmounts = useMemo(
    () => buildLineItemAmounts(includedLineItems),
    [includedLineItems],
  )

  const hasSelectableOptions = useMemo(
    () => lineItems.some((lineItem) => lineItem.selectionMode !== 'required'),
    [lineItems],
  )

  const isItemAvailable = useCallback(
    (lineItem: PublicQuoteLineItem): boolean => {
      const prerequisites = lineItem.requiresAnyOf ?? []
      return (
        prerequisites.length === 0 ||
        prerequisites.some((prerequisiteId) => includedIdSet.has(prerequisiteId))
      )
    },
    [includedIdSet],
  )

  const toggleOptionalItem = useCallback((lineItemId: string): void => {
    setSelectedIds((previous) =>
      previous.includes(lineItemId)
        ? previous.filter((id) => id !== lineItemId)
        : [...previous, lineItemId],
    )
  }, [])

  const chooseGroupOption = useCallback(
    (lineItem: PublicQuoteLineItem): void => {
      const groupItemIds = lineItems
        .filter(
          (candidate) =>
            candidate.selectionMode === 'choose-one' &&
            candidate.optionGroup === lineItem.optionGroup,
        )
        .map((candidate) => candidate.id)

      setSelectedIds((previous) => [
        ...previous.filter((id) => !groupItemIds.includes(id)),
        lineItem.id,
      ])
    },
    [lineItems],
  )

  if (loadState === 'loading') {
    return (
      <PageBackground $brand={brand} $accent={accent}>
        <StateCard>Loading your quote...</StateCard>
      </PageBackground>
    )
  }

  if (loadState === 'expired') {
    return (
      <PageBackground $brand={brand} $accent={accent}>
        <StateCard>
          This quote link has expired.
          {requestState === 'sent' ? (
            <RequestSentNote>
              Request sent! The sender has been notified and will send you a
              fresh link shortly.
            </RequestSentNote>
          ) : (
            <RequestLinkForm>
              <p>
                Request a new link below and the sender will be notified right
                away. No email or phone call needed.
              </p>
              <CommentTextarea
                value={requestMessage}
                placeholder="Add a message for the sender (optional)..."
                onChange={event => setRequestMessage(event.target.value)}
              />
              {requestState === 'error' ? (
                <RequestErrorNote>
                  Something went wrong. Please try again.
                </RequestErrorNote>
              ) : null}
              <PrimaryButton
                onClick={handleRequestLink}
                disabled={requestState === 'sending'}>
                {requestState === 'sending' ? 'Sending...' : 'Request New Link'}
              </PrimaryButton>
            </RequestLinkForm>
          )}
        </StateCard>
      </PageBackground>
    )
  }

  if (loadState === 'notfound' || loadState === 'error' || !payload) {
    return (
      <PageBackground $brand={brand} $accent={accent}>
        <StateCard>
          We couldn&apos;t find that quote. Please double-check your link.
        </StateCard>
      </PageBackground>
    )
  }

  const currency = payload.currencyCode
  const effectiveStatus =
    isPreview && previewDecision !== null ? previewDecision : payload.status
  const effectiveHasApproved =
    payload.recipient.hasApproved ||
    (isPreview && previewDecision === 'approved')
  const effectiveHasDeclined =
    payload.recipient.hasDeclined ||
    (isPreview && previewDecision === 'declined')
  const isResolved =
    effectiveStatus === 'approved' || effectiveStatus === 'declined'
  const isLocked = payload.isLocked === true
  const canDecide =
    !isLocked &&
    !effectiveHasApproved &&
    !effectiveHasDeclined &&
    effectiveStatus !== 'approved'
  const isSelectionInteractive = hasSelectableOptions && canDecide
  const showLockedBanner =
    isLocked && !effectiveHasApproved && !effectiveHasDeclined
  const showPayButton =
    effectiveStatus === 'approved' && payload.paymentUrl.length > 0
  const displayComments = isPreview
    ? [...payload.comments, ...previewComments]
    : payload.comments
  const senderLabel =
    payload.agentName.length > 0 ? payload.agentName : payload.companyName
  const showComposer = !isResolved || effectiveHasApproved

  const describePrerequisites = (lineItem: PublicQuoteLineItem): string =>
    (lineItem.requiresAnyOf ?? [])
      .map(
        (prerequisiteId) =>
          lineItems.find((candidate) => candidate.id === prerequisiteId)
            ?.description,
      )
      .filter((description): description is string => Boolean(description))
      .join(' or ')

  const renderSelectControl = (
    lineItem: PublicQuoteLineItem,
    included: boolean,
    available: boolean,
  ) => {
    if (lineItem.selectionMode === 'required') {
      return <input type="checkbox" checked={included} disabled readOnly />
    }
    if (lineItem.selectionMode === 'choose-one') {
      return (
        <input
          type="radio"
          name={`group-${lineItem.optionGroup}`}
          checked={included}
          disabled={!isSelectionInteractive || !available}
          onChange={() => chooseGroupOption(lineItem)}
        />
      )
    }
    return (
      <input
        type="checkbox"
        checked={included}
        disabled={!isSelectionInteractive || !available}
        onChange={() => toggleOptionalItem(lineItem.id)}
      />
    )
  }

  const briefSlides = payload.brief?.slides ?? []
  const hasBrief = briefSlides.length > 0
  const isPresentation = payload.briefViewMode === 'presentation'
  const activeSlide = Math.min(slideIndex, Math.max(0, briefSlides.length - 1))

  let commentsEmptyMessage =
    'No questions yet. Ask anything or request a change below.'
  if (effectiveHasApproved) {
    commentsEmptyMessage =
      'No messages yet. Send a note below to get next steps rolling.'
  } else if (isPreview && !isResolved) {
    commentsEmptyMessage =
      'This is where your recipient can ask questions or request changes.'
  } else if (isResolved) {
    commentsEmptyMessage = 'No questions were asked.'
  }

  return (
    <PageBackground $brand={brand} $accent={accent}>
      <PageContent>
        {isPreview ? (
          <PreviewBanner>
            Preview mode. This is exactly what your recipient sees, and you can
            try the whole flow. Nothing is recorded.
            {previewDecision !== null || previewComments.length > 0 ? (
              <PreviewResetButton onClick={handlePreviewReset}>
                Start over
              </PreviewResetButton>
            ) : null}
          </PreviewBanner>
        ) : null}
        <BrandHeader>
          {payload.companyLogoUrl.length > 0 ? (
            <BrandLogo src={payload.companyLogoUrl} alt={payload.companyName} />
          ) : (
            <BrandName>{payload.companyName}</BrandName>
          )}
        </BrandHeader>
        {payload.agentName.length > 0 ? (
          <SentBy>Sent by {payload.agentName}</SentBy>
        ) : null}

        {effectiveHasApproved ? (
          <ResultBanner $tone="approved">
            You approved this quote. Thank you
            {isPreview ? '' : `, ${payload.recipient.name}`}!
          </ResultBanner>
        ) : null}
        {effectiveHasDeclined ? (
          <ResultBanner $tone="declined">You declined this quote.</ResultBanner>
        ) : null}
        {showLockedBanner ? (
          <LockedBanner>
            This quote is locked, so it can no longer be approved or declined.
          </LockedBanner>
        ) : null}

        {hasBrief && payload.introMessage.length > 0 ? (
          <IntroText>{payload.introMessage}</IntroText>
        ) : null}

        {hasBrief && isPresentation ? (
          <BriefStage>
            <BriefSlide
              src={briefSlides[activeSlide].imageUrl}
              alt={`Brief slide ${activeSlide + 1}`}
            />
            {briefSlides.length > 1 ? (
              <BriefNav>
                <BriefNavButton
                  onClick={() =>
                    setSlideIndex(previous => Math.max(0, previous - 1))
                  }
                  disabled={activeSlide === 0}>
                  Previous
                </BriefNavButton>
                <BriefCounter>
                  {activeSlide + 1} / {briefSlides.length}
                </BriefCounter>
                <BriefNavButton
                  onClick={() =>
                    setSlideIndex(previous =>
                      Math.min(briefSlides.length - 1, previous + 1),
                    )
                  }
                  disabled={activeSlide >= briefSlides.length - 1}>
                  Next
                </BriefNavButton>
              </BriefNav>
            ) : null}
          </BriefStage>
        ) : null}
        {hasBrief && !isPresentation ? (
          <BriefDeck>
            {briefSlides.map((slide, index) => (
              <BriefSlide
                key={slide.imageUrl}
                src={slide.imageUrl}
                alt={`Brief slide ${index + 1}`}
              />
            ))}
          </BriefDeck>
        ) : null}

        <QuoteCard>
          <QuoteHeader>
            <QuoteTitle>{payload.title}</QuoteTitle>
            <VersionTag>Version {payload.versionNumber}</VersionTag>
          </QuoteHeader>

          {payload.introMessage.length > 0 ? (
            <IntroText>{payload.introMessage}</IntroText>
          ) : null}

          {isSelectionInteractive ? (
            <SelectionHint>
              This quote has options. <strong>Choose what you need</strong> and
              your total updates automatically. Your choices are saved when you
              approve.
            </SelectionHint>
          ) : null}

          <ItemsTable>
            <thead>
              <tr>
                {hasSelectableOptions ? (
                  <th className="select-cell">Include</th>
                ) : null}
                <th>Description</th>
                <th className="numeric">Qty</th>
                <th className="numeric">Unit Price</th>
                <th className="numeric">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payload.lineItems.map(lineItem => {
                const isIncluded = includedIdSet.has(lineItem.id)
                const isAvailable = isItemAvailable(lineItem)
                const prerequisiteLabel = describePrerequisites(lineItem)
                const isDiscount = lineItem.kind === 'discount'
                const amount =
                  lineItemAmounts.get(lineItem.id) ??
                  lineItem.quantity * lineItem.unitPrice
                const quantityDisplay = isDiscount
                  ? '—'
                  : String(lineItem.quantity)
                let unitPriceDisplay = formatCurrency(
                  lineItem.unitPrice,
                  currency,
                )
                if (isDiscount && lineItem.discountMode === 'percent') {
                  unitPriceDisplay = `-${lineItem.discountValue ?? 0}%`
                } else if (isDiscount) {
                  unitPriceDisplay = '—'
                }

                return (
                  <tr
                    key={lineItem.id}
                    className={
                      hasSelectableOptions && !isIncluded ? 'excluded' : undefined
                    }>
                    {hasSelectableOptions ? (
                      <td className="select-cell">
                        {renderSelectControl(lineItem, isIncluded, isAvailable)}
                      </td>
                    ) : null}
                    <td className="description-cell">
                      {lineItem.description}
                      {!isAvailable && prerequisiteLabel.length > 0 ? (
                        <span className="needs-hint">
                          Included when you select: {prerequisiteLabel}
                        </span>
                      ) : null}
                    </td>
                    <td className="numeric" data-label="Qty">
                      {quantityDisplay}
                    </td>
                    <td className="numeric" data-label="Unit Price">
                      {unitPriceDisplay}
                    </td>
                    <td className="numeric" data-label="Amount">
                      {formatCurrency(amount, currency)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </ItemsTable>

          <TotalsBlock>
            <TotalsRow>
              <span>Subtotal</span>
              <span>{formatCurrency(liveTotals.subtotal, currency)}</span>
            </TotalsRow>
            {liveTotals.discountAmount > 0 ? (
              <TotalsRow>
                <span>Discount</span>
                <span>
                  -{formatCurrency(liveTotals.discountAmount, currency)}
                </span>
              </TotalsRow>
            ) : null}
            {liveTotals.taxRatePercent > 0 ? (
              <TotalsRow>
                <span>Tax ({liveTotals.taxRatePercent}%)</span>
                <span>{formatCurrency(liveTotals.taxAmount, currency)}</span>
              </TotalsRow>
            ) : null}
            <TotalsRow $grand={true}>
              <span>Total</span>
              <span>{formatCurrency(liveTotals.total, currency)}</span>
            </TotalsRow>
          </TotalsBlock>

          {payload.notes.length > 0 ? (
            <NotesBlock>
              <h3>Notes</h3>
              <p>{payload.notes}</p>
            </NotesBlock>
          ) : null}

          <ActionsRow>
            {payload.pdfUrl.length > 0 ? (
              <PdfLink href={payload.pdfUrl} target="_blank" rel="noreferrer">
                Download PDF
              </PdfLink>
            ) : null}
            {canDecide ? (
              <>
                <SuccessButton
                  onClick={() => handleDecision('approve')}
                  disabled={isSubmittingDecision}>
                  Approve Quote
                </SuccessButton>
                <OutlineButton
                  onClick={() => handleDecision('decline')}
                  disabled={isSubmittingDecision}>
                  Decline
                </OutlineButton>
              </>
            ) : null}
            {showPayButton ? (
              <PrimaryButton
                onClick={() => window.open(payload.paymentUrl, '_blank')}
                disabled={payload.paymentStatus === 'paid'}>
                {payload.paymentStatus === 'paid' ? 'Paid' : 'Pay Now'}
              </PrimaryButton>
            ) : null}
          </ActionsRow>
        </QuoteCard>

        <QuoteCard>
          <QuoteTitle>
            {effectiveHasApproved
              ? 'Get next steps rolling'
              : 'Questions or changes?'}
          </QuoteTitle>
          <CommentThread>
            {displayComments.length === 0 ? (
              <CommentEmpty>{commentsEmptyMessage}</CommentEmpty>
            ) : null}
            {displayComments.map(comment => (
              <CommentBubble
                key={`${comment.createdAt}-${comment.authorName}`}
                $isClient={comment.authorType === 'client'}>
                <CommentAuthor>{comment.authorName}</CommentAuthor>
                <CommentBody>{comment.body}</CommentBody>
              </CommentBubble>
            ))}
          </CommentThread>
          {showComposer ? (
            <ActionsRow>
              <CommentTextarea
                value={commentBody}
                placeholder={
                  effectiveHasApproved
                    ? `Send ${senderLabel} a message about next steps...`
                    : 'Ask a question or request a change...'
                }
                onChange={event => setCommentBody(event.target.value)}
              />
              <PrimaryButton
                onClick={handleAddComment}
                disabled={isSubmittingComment}>
                Send
              </PrimaryButton>
            </ActionsRow>
          ) : null}
        </QuoteCard>
      </PageContent>
    </PageBackground>
  )
}

export default QuotePage
